import google, { canvasProjection, getArray, isMVCArray, toLatLng, toLatLngBounds } from "@churchofjesuschrist/maps-google";
import { defaultEmpty, isArray, isNumber } from "@churchofjesuschrist/maps-utils";
import { AreaUnitType, closestPositionOnSegment, convertUnit, isPointInRange, LengthUnitType, pushAllLongitude, roundTo, someCrossesAntiMeridian } from "@churchofjesuschrist/maps-math";
import { degreeNearlyEqualsTolerance, mapExtentProjected, mapSides } from "@churchofjesuschrist/maps-constants";
export const isPositionInRange = (origin, position, range, reference) => {
  let projection = canvasProjection(reference),
    originPoint = projection ? projection.fromLatLngToContainerPixel(toLatLng(origin)) : null,
    positionPoint = projection ? projection.fromLatLngToContainerPixel(toLatLng(position)) : null;
  return originPoint && positionPoint && isPointInRange(originPoint, positionPoint, range);
};
export const isPointBetween = (point, a, b) => {
  return isInRange(point.x, a.x, b.x) && isInRange(point.y, a.y, b.y);
};
export const isInRange = (value, a, b) => {
  a = roundTo(a, 2);
  b = roundTo(b, 2);
  value = roundTo(value, 2);
  return a < b ? a <= value && value <= b : a >= value && value >= b;
};
export const isLocationBetween = (point, pointA, pointB, tolerance = degreeNearlyEqualsTolerance) => {
  let line = new google.maps.Polyline({
    path: [pointA, pointB]
  });
  return google.maps.geometry.poly.isLocationOnEdge(point, line, tolerance);
};
export const clonePath = path => {
  return new google.maps.MVCArray(path.getArray());
};
export const clonePaths = paths => {
  return new google.maps.MVCArray(paths.getArray().map(clonePath));
};
export const getPathsBounds = paths => {
  let bounds = new google.maps.LatLngBounds();
  if (paths) {
    paths = getArray(paths);
    paths.forEach(current => {
      if (isMVCArray(current)) {
        current = current.getArray();
      }
      if (isArray(current)) {
        current.forEach(entry => {
          bounds.extend(entry);
        });
      } else {
        bounds.extend(current);
      }
    });
  }
  return bounds;
};
export const getBoundsPath = (bounds, closePath = false) => {
  let latLngBounds = toLatLngBounds(bounds),
    start = getBoundsNorthWest(latLngBounds),
    corners = [start, latLngBounds.getNorthEast(), getBoundsSouthEast(latLngBounds), latLngBounds.getSouthWest()];
  if (closePath) {
    corners.push(start);
  }
  return new google.maps.MVCArray(corners);
};
export const getBoundsMidPath = (bounds, closePath = false) => {
  let path = getBoundsPath(bounds, true),
    length = path.getLength(),
    newPath = new google.maps.MVCArray();
  for (let i = 1; i < length; i++) {
    let vertexA = path.getAt(i - 1),
      vertexB = path.getAt(i),
      midPoint = google.maps.geometry.spherical.interpolate(vertexA, vertexB, 0.5);
    newPath.push(midPoint);
  }
  if (closePath) {
    newPath.push(newPath.getAt(0));
  }
  return newPath;
};
export const getBoundsNorth = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return latLngBounds.getNorthEast().lat();
};
export const getBoundsSouth = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return latLngBounds.getSouthWest().lat();
};
export const getBoundsEast = (bounds, noWrap = false) => {
  let latLngBounds = toLatLngBounds(bounds);
  let east = latLngBounds.getNorthEast().lng();
  if (noWrap && east > getBoundsWest(latLngBounds)) {
    east -= 360.0;
  }
  return east;
};
export const getBoundsWest = (bounds, noWrap = false) => {
  let latLngBounds = toLatLngBounds(bounds);
  let west = latLngBounds.getSouthWest().lng();
  if (noWrap && west > getBoundsEast(latLngBounds)) {
    west -= 360.0;
  }
  return west;
};
export const getBoundsNorthWest = bounds => {
  return new google.maps.LatLng(getBoundsNorth(bounds), getBoundsWest(bounds));
};
export const getBoundsSouthEast = bounds => {
  return new google.maps.LatLng(getBoundsSouth(bounds), getBoundsEast(bounds));
};
export const getBoundsNorthEast = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return latLngBounds.getNorthEast();
};
export const getBoundsSouthWest = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return latLngBounds.getSouthWest();
};
export const getBoundsWidth = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return google.maps.geometry.spherical.computeDistanceBetween(latLngBounds.getSouthWest(), getBoundsSouthEast(latLngBounds));
};
export const getBoundsHeight = bounds => {
  let latLngBounds = toLatLngBounds(bounds);
  return google.maps.geometry.spherical.computeDistanceBetween(latLngBounds.getSouthWest(), getBoundsNorthWest(latLngBounds));
};
export const boundsCrossesAntiMeridian = bounds => {
  let west = getBoundsWest(bounds),
    east = getBoundsEast(bounds);
  return west > east && west !== mapExtentProjected.east;
};
export const splitBoundsOnAntiMeridian = bounds => {
  let north = getBoundsNorth(bounds),
    south = getBoundsSouth(bounds),
    east = getBoundsEast(bounds),
    west = getBoundsWest(bounds);
  return {
    [mapSides.left]: toLatLngBounds([west, south, mapExtentProjected.east, north]),
    [mapSides.right]: toLatLngBounds([mapExtentProjected.west, south, east, north])
  };
};
export const containsBounds = (bounds, other, noWrap = false) => {
  if (boundsCrossesAntiMeridian(bounds)) {
    let split = splitBoundsOnAntiMeridian(bounds);
    return Object.values(split).some(current => containsBounds(current, other, true));
  } else {
    return getBoundsNorth(bounds) >= getBoundsNorth(other) && getBoundsSouth(bounds) <= getBoundsSouth(other) && getBoundsEast(bounds) >= getBoundsEast(other) && getBoundsWest(bounds, noWrap) <= getBoundsWest(other);
  }
};
export const recalculateBounds = (bounds, {
  width,
  height,
  perimeter,
  area,
  length
}, unit = null) => {
  let center = bounds.getCenter(),
    currentWidth = getBoundsWidth(bounds),
    currentHeight = getBoundsHeight(bounds),
    newWidth = null,
    newHeight = null,
    fromArea = false;
  if (isNumber(width) || isNumber(height)) {
    newWidth = defaultEmpty(width);
    newHeight = defaultEmpty(height);
  } else {
    let ratio = currentWidth / currentHeight;
    perimeter = [perimeter, length].find(isNumber);
    if (isNumber(perimeter)) {
      newWidth = perimeter / (2 + 2.0 / ratio);
      newHeight = perimeter / (2 + 2.0 * ratio);
    } else if (isNumber(area)) {
      if (unit) {
        area = convertUnit(area, unit, AreaUnitType.SQUARE_METER);
      }
      newWidth = Math.sqrt(area * ratio);
      newHeight = Math.sqrt(area / ratio);
      fromArea = true;
    }
  }
  if (unit && !fromArea) {
    if (isNumber(newWidth)) {
      newWidth = convertUnit(newWidth, unit, LengthUnitType.METER);
    }
    if (isNumber(newHeight)) {
      newHeight = convertUnit(newHeight, unit, LengthUnitType.METER);
    }
  }
  newWidth = defaultEmpty(newWidth, currentWidth);
  newHeight = defaultEmpty(newHeight, currentHeight);
  let results = {
    east: google.maps.geometry.spherical.computeOffset(center, newWidth / 2, 90).lng(),
    west: google.maps.geometry.spherical.computeOffset(center, newWidth / 2, -90).lng(),
    north: google.maps.geometry.spherical.computeOffset(center, newHeight / 2, 0).lat(),
    south: google.maps.geometry.spherical.computeOffset(center, newHeight / 2, 180).lat()
  };
  return toLatLngBounds(results);
};
export const getCirclePath = circle => {
  let radius = circle.getRadius(),
    center = circle.getCenter(),
    steps = Math.min(Math.max(Math.ceil(radius / 100), 36), 360),
    path = [];
  for (let i = 0; i < steps; i++) {
    let heading = 360.0 * (i / steps);
    path.push(google.maps.geometry.spherical.computeOffset(center, radius, heading));
  }
  return new google.maps.MVCArray(path);
};
export const getPartialCirclePath = (circle, startHeading, endHeading) => {
  let radius = circle.getRadius(),
    center = circle.getCenter(),
    crosses = startHeading > endHeading,
    steps = Math.min(Math.max(Math.ceil(radius / 100), 36), 360),
    path = [];
  for (let i = 0; i < steps; i++) {
    let heading = 360.0 * (i / steps),
      valid = crosses ? startHeading <= heading || heading <= endHeading : startHeading <= heading && heading <= endHeading;
    if (valid) {
      path.push(google.maps.geometry.spherical.computeOffset(center, radius, heading));
    }
  }
  return closeLoop(new google.maps.MVCArray(path));
};
export const calculateCircleRadius = ({
  radius,
  diameter,
  circumference,
  area,
  width,
  height,
  perimeter,
  length
}, unit = null) => {
  let newRadius = null,
    fromArea = false;
  if (isNumber(radius)) {
    newRadius = radius;
  } else {
    diameter = [diameter, width, height].find(isNumber);
    if (isNumber(diameter)) {
      newRadius = diameter / 2.0;
    } else {
      circumference = [circumference, perimeter, length].find(isNumber);
      if (isNumber(circumference)) {
        newRadius = circumference / (2.0 * Math.PI);
      } else if (isNumber(area)) {
        if (unit) {
          area = convertUnit(area, unit, AreaUnitType.SQUARE_METER);
        }
        //TODO: is this math correct for over a sphere?
        newRadius = Math.sqrt(area / Math.PI);
        fromArea = true;
      }
    }
  }
  if (unit && !fromArea) {
    newRadius = convertUnit(newRadius, unit, LengthUnitType.METER);
  }
  return newRadius;
};
export const separatePolygonPaths = (polygon, closePaths = false) => {
  let separatePaths = [],
    paths = polygon.getPaths();
  if (closePaths) {
    paths = closeLoops(paths);
  }
  paths = paths.getArray();
  let firstPath = paths[0];
  separatePaths.push(new google.maps.MVCArray([firstPath]));
  if (paths.length > 1) {
    // check path for different clockwise rotation to determine if inner
    //TODO: if contained in other path to determine which to group with (if none, group with first outer?)
    let outerDirection = isClockwise(firstPath);
    for (let i = 1; i < paths.length; i++) {
      let path = paths[i],
        cw = isClockwise(path);
      if (cw === outerDirection) {
        separatePaths.push(new google.maps.MVCArray([path]));
      } else {
        separatePaths[separatePaths.length - 1].push(path);
      }
    }
  }
  return separatePaths;
};
export const isClockwise = path => {
  let positions = getPathJSON(path);
  if (someCrossesAntiMeridian(positions)) {
    positions = pushAllLongitude(positions);
    path = getLatLng(positions);
  }
  let area = google.maps.geometry.spherical.computeSignedArea(path);
  return area <= 0;
};
const getLatLng = position => {
  if (isArray(position)) {
    return position.map(getLatLng);
  } else {
    return new google.maps.LatLng(position.latitude, position.longitude, true);
  }
};
export const getPathJSON = path => {
  return getArray(path).map(position => position.toJSON());
};
export const getPolygonArea = polygon => {
  let paths = polygon.getPaths();
  if (paths.getLength() === 1) {
    return google.maps.geometry.spherical.computeArea(polygon.getPath());
  } else {
    let area = paths.getArray().reduce((area, path) => {
      area += google.maps.geometry.spherical.computeSignedArea(path);
      return area;
    }, 0);
    return Math.abs(area);
  }
};
export const getPolygonPerimeter = polygon => {
  let paths = polygon.getPaths();
  return paths.getArray().reduce((perimeter, path) => {
    perimeter += google.maps.geometry.spherical.computeLength(closeLoop(path));
    return perimeter;
  }, 0);
};
export const getClosestPositionOnCircle = (circle, position) => {
  let radius = circle.getRadius(),
    center = circle.getCenter(),
    heading = google.maps.geometry.spherical.computeHeading(center, position),
    offset = google.maps.geometry.spherical.computeOffset(center, radius, heading),
    distance = google.maps.geometry.spherical.computeDistanceBetween(offset, position);
  return {
    position: offset,
    distance
  };
};
export const getClosestPositionOnPaths = (paths, position) => {
  let closest = null,
    pathsIndex = null,
    pathIndex = null,
    distance = null;
  paths.forEach((currentPath, currentPathIndex) => {
    let length = currentPath.getLength();
    if (length > 1) {
      for (let i = 1; i < length; i++) {
        let vertexA = currentPath.getAt(i - 1),
          vertexB = currentPath.getAt(i),
          currentClosest = getLatLng(closestPositionOnSegment(vertexA.toJSON(), vertexB.toJSON(), position.toJSON())),
          currentDistance = google.maps.geometry.spherical.computeDistanceBetween(currentClosest, position);
        if (distance === null || distance > currentDistance) {
          distance = currentDistance;
          closest = currentClosest;
          pathsIndex = currentPathIndex;
          pathIndex = i - 1;
        }
      }
    } else if (length) {
      let currentClosest = currentPath.getAt(0),
        pointDistance = google.maps.geometry.spherical.computeDistanceBetween(currentClosest, position);
      if (distance === null || distance > pointDistance) {
        distance = currentClosest;
        closest = currentClosest;
        pathsIndex = currentPathIndex;
        pathIndex = 0;
      }
    }
  });
  return {
    position: closest,
    pathsIndex,
    pathIndex,
    distance
  };
};
export const closeLoop = path => {
  let loop = getArray(path),
    first = loop[0],
    last = loop[path.length - 1];
  if (first.equals(last)) {
    return path;
  } else {
    return new google.maps.MVCArray([...loop, first]);
  }
};
export const closeLoops = paths => {
  let loops = getArray(paths);
  return new google.maps.MVCArray(loops.map(closeLoop));
};
export const openLoop = path => {
  let loop = getArray(path),
    first = loop[0],
    last = loop[path.length - 1];
  if (first.equals(last)) {
    let updated = [...loop];
    updated.pop();
    return new google.maps.MVCArray(updated);
  } else {
    return path;
  }
};
export const openLoops = paths => {
  let loops = getArray(paths);
  return new google.maps.MVCArray(loops.map(openLoop));
};